import { BranchCompareRegular } from '@fluentui/react-icons';
import { TOptionsDialogState } from '@shared/types';
import { IMPACT_ANALYSIS_OPTIONS, IMPACTS_NOTIFICATIONS } from '../constants';
import { EItemScreenMode, isTDraft, useItemScreenStore } from '@modules/items';
import { AccessType, TPermissions } from '@modules/access';
import { TOverflowButtonParam } from '@shared/components';
import { TDraft, TVersion } from '@modules/documents';
import { TFileVersion } from '@modules/files';

type TProps = {
  version?: TDraft | TVersion | TFileVersion;
  setOptionsDialogState: (state: TOptionsDialogState | undefined) => void;
  permissions: TPermissions;
};

const useImpactAnalysisNavAction = ({ version, setOptionsDialogState, permissions }: TProps) => {
  const { setScreenMode } = useItemScreenStore();

  const hasEditPermissions = permissions.accessRight >= AccessType.EDIT;

  const impactAnalysisNavActionButton: TOverflowButtonParam = {
    children: 'Analyze impact',
    icon: <BranchCompareRegular />,
    onClick: () => {
      // post-change analysis diabled for draft or if the user has no edit-permissions:
      const disabledMessagePostChange = isTDraft(version)
        ? IMPACTS_NOTIFICATIONS.POST_CHANGE_NOT_POSSIBLE_FOR_DRAFT
        : !version?.impactAnalysis && !hasEditPermissions
          ? IMPACTS_NOTIFICATIONS.POST_CHANGE_NO_EDIT_PERMISSIONS
          : undefined;

      const dialogState: TOptionsDialogState = {
        isOpen: true,
        title: (
          <>
            <BranchCompareRegular fontSize={28} className="mb-1" /> Analyze impact
          </>
        ),
        label: 'Select mode:',
        options: [
          {
            ...IMPACT_ANALYSIS_OPTIONS.PRE_CHANGE,
            subtitle: 'Analyze impact before change',
            onClick: () => {
              setScreenMode(EItemScreenMode.PRE_CHANGE_IMPACT_ANALYSIS);
              setOptionsDialogState({ ...dialogState, isOpen: false });
            },
          },
          {
            ...IMPACT_ANALYSIS_OPTIONS.POST_CHANGE,
            subtitle: `Analyze impact of version ${(version as TVersion | TVersion | undefined)?.id || ''}`,

            tooltip: disabledMessagePostChange,
            disabled: Boolean(disabledMessagePostChange),
            onClick: () => {
              if (isTDraft(version)) return; // cannot happen

              // Open the results screen if the analysis has already been completed
              if (version?.impactAnalysis) {
                setScreenMode(EItemScreenMode.IMPACT_ANALYSIS_SUMMARY);
              } else {
                setScreenMode(EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS);
              }

              // Close the dialog
              setOptionsDialogState({ ...dialogState, isOpen: false });
            },
          },
        ],
      };
      setOptionsDialogState(dialogState);
    },
  };

  return {
    impactAnalysisNavActionButton,
  };
};

export default useImpactAnalysisNavAction;
